import { NestAPI, useGetBusiness } from '@marketing-milk/frontend'
import {
  CreateSprintPacket,
  SprintPacket,
  SprintPacketHydrated,
  UpdateSprintPacket,
} from '@marketing-milk/interfaces'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

type Body<T> = { body: T }

export const useSprintPackets = () => {
  const business = useGetBusiness()
  const endpoint = `businesses/${business.id}/sprint-packets`

  const {
    data: list,
    refetch,
    isLoading,
    isFetched,
    isError,
  } = useQuery(
    ['sprint-packets', business.id],
    async () => {
      const response = await NestAPI.get<Body<SprintPacket[]>>(endpoint)
      return response.data.body
    },
    { retry: false }
  )

  const create = async (dto: CreateSprintPacket): Promise<SprintPacket | undefined> => {
    try {
      const response = await NestAPI.post<Body<SprintPacket>>(endpoint, dto)
      await refetch()
      return response.data.body
    } catch (err) {
      toast.error(`Create action failed: ${err}`)
      return undefined
    }
  }

  return { list, create, refetch, isLoading, isFetched, isError }
}

export const useSprintPacket = (packetID: number | undefined) => {
  const business = useGetBusiness()

  const {
    data: packet,
    refetch,
    isLoading,
    isFetched,
    isError,
  } = useQuery(
    ['sprint-packet', business.id, packetID],
    async () => {
      const response = await NestAPI.get<Body<SprintPacketHydrated>>(
        `businesses/${business.id}/sprint-packets/${packetID}`
      )
      return response.data.body
    },
    { retry: false, enabled: typeof packetID === 'number' && !Number.isNaN(packetID) }
  )

  return { packet, refetch, isLoading, isFetched, isError }
}

export const useUpdateSprintPacket = (packetID: number | undefined) => {
  const business = useGetBusiness()
  const queryClient = useQueryClient()

  return useMutation(
    async (dto: UpdateSprintPacket): Promise<SprintPacket> => {
      const response = await NestAPI.patch<Body<SprintPacket>>(
        `businesses/${business.id}/sprint-packets/${packetID}`,
        dto
      )
      return response.data.body
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sprint-packet', business.id, packetID])
      },
      onError: err => {
        toast.error(`Save failed: ${err}`)
      },
    }
  )
}
